import useIsServer from 'hooks/useIsServer';
import React from 'react';
import { RouteConfig } from 'react-router-config';
import { Category, OpRoute } from 'store/app/types';
import SSRParams from 'utils/class/SSRParams';
import getRouteParamValue from 'utils/routes/getRouteParamValue';
import BasketPage from 'views/BasketPage/BasketPage';
import CategoryPage, { fetchCategoryPageInitialData } from 'views/CategoryPage/CategoryPage';
import PreviewPage, { fetchPreviewPageInitialData } from 'views/Contentful/PreviewPage';
import FavoritesPage from 'views/FavoritesPage/FavoritesPage';
import HighlightsPage from 'views/HighlightsPage/HighlightsPage';
import LandingPage, { fetchLandingPageInitialData } from 'views/LandingPages/LandingPage';
import PageNotFound from 'views/PageNotFound/PageNotFound';
import PersonalisedProductPage, {
  fetchPersonalisedProductPageInitialData,
} from 'views/PersonalisedProductPage/PersonalisedProductPage';
import PlusMembershipPage from 'views/PlusMembershipPage/PlusMembershipPage';
import PlusReceiptsPage from 'views/PlusReceiptsPage/PlusReceiptsPage';
import ProductPage, { fetchProductPageInitialData } from 'views/ProductPage/ProductPage';
import SearchCategoryPage, { fetchSearchCategoryPageInitialData } from 'views/SearchCategoryPage/SearchCategoryPage';
import SearchPage, { fetchSearchPageInitialData } from 'views/SearchPage/SearchPage';
import { fetchStudioSearchPageInitialData } from 'views/StudioSearchPage/StudioSearchPage';
import StudioSearchPageRoute from 'views/StudioSearchPage/StudioSearchPageRoute';
import StudioSearchProductPage from 'views/StudioSearchProductPage/StudioSearchProductPage';

export const mapParameters = (parameters: { name: string; value: any }[]) => {
  return parameters.reduce((m, { name, value }) => m.set(name, value), new Map<string, any>());
};

export enum RouteType {
  BASKET = 1,
  CHECKOUT = 2,
  PRODUCT_PAGE = 3,
  CATEGORY_PAGE = 4,
  SEARCH_PAGE = 5,
  LANDING_PAGE = 6,
  PERSONALIZED_PRODUCT_PAGE = 7,
  REACT_PAGE = 8,
  STUDIO_SEARCH_PAGE = 9,
  ROUTE = 10,
}

export interface Route extends RouteConfig {
  component: React.ComponentType<any>;
  fetchInitialData?: (params: SSRParams) => Promise<any>[];
  categoryId?: number;
  productUid?: string;
  productName?: string;
  designId?: string;
  landingPageName?: string;
  isPersonalised?: boolean;
  mappingId?: string;
  path: string;
  parameters?: { name: string; value: string | number }[];
}

const buildPageNotFoundRoute = (): Route => ({
  path: '/404',
  component: PageNotFound,
  name: 'PageNotFound',
  exact: true,
});

const buildLocalizedRoute = (route: Route): Route[] => [
  route,
  {
    ...route,
    path: `/:lang${route.path}`,
  },
];

const initialRoutes: Route[] = [
  {
    path: '/contentful/preview/:contentType/:entryId',
    component: PreviewPage,
    name: 'ContentfulPreviewPage',
    exact: true,
    fetchInitialData: fetchPreviewPageInitialData,
  },
  ...buildLocalizedRoute({
    path: '/search',
    component: SearchPage,
    name: 'SearchPage',
    exact: true,
    fetchInitialData: fetchSearchPageInitialData,
  }),
  ...buildLocalizedRoute({
    path: '/products/:slug',
    component: StudioSearchProductPage,
    name: 'SearchCategoryProductPage',
    exact: true,
    fetchInitialData: undefined,
  }),
  ...buildLocalizedRoute({
    path: '/products/:slug/:preselectProductSlug',
    component: StudioSearchProductPage,
    name: 'SearchCategoryProductPage',
    exact: true,
    fetchInitialData: undefined,
  }),
  ...buildLocalizedRoute({
    path: '/studio-selection/getty/search',
    component: StudioSearchPageRoute,
    name: 'StudioSearchPage',
    exact: true,
    fetchInitialData: fetchStudioSearchPageInitialData,
  }),
  ...buildLocalizedRoute({
    component: LandingPage,
    path: '/studio-selection/wallpaper',
    name: `LandingPage_StudioSelectionWallpaper`,
    exact: true,
    fetchInitialData: fetchLandingPageInitialData({ categoryId: '2984' }),
  }),
  ...buildLocalizedRoute({
    path: '/my-subscription',
    name: `PlusMembershipPage`,
    exact: true,
    component: PlusMembershipPage,
  }),
  {
    path: '/compare-favourites',
    mappingId: `products.FavoritesProducts`,
    name: `Favourites`,
    exact: true,
    component: FavoritesPage,
  },
  ...buildLocalizedRoute({
    path: '/highlights',
    mappingId: `products.customerHighlights`,
    name: `Highlights`,
    exact: true,
    component: HighlightsPage,
  }),
  {
    path: '/plus-receipts',
    name: `PlusMembershipPage`,
    exact: true,
    component: PlusReceiptsPage,
  },
  buildPageNotFoundRoute(),
];

const buildCategoryRoutes = (categories: Category[]): Route[] => {
  const isServer = useIsServer();
  return categories.map((category) => ({
    categoryId: category.categoryId,
    path: category.url,
    name: `CategoryPage_${category.categoryId}`,
    exact: true,
    component: category.isSearchBasedCategory ? SearchCategoryPage : CategoryPage,
    fetchInitialData: isServer ? fetchCategoryPageInitialData : undefined,
  }));
};

const buildPersonalisedProductsRoute = (route: OpRoute): Route => {
  const isServer = useIsServer();
  return {
    productName: getRouteParamValue(route as any, 'productName'),
    productUid: getRouteParamValue(route as any, 'productUid'),
    path: route.path,
    name: `PersonalisedProductPage_${route.mappingId}`,
    exact: true,
    isPersonalised: true,
    component: PersonalisedProductPage,
    fetchInitialData: isServer ? fetchPersonalisedProductPageInitialData : undefined,
  };
};

const buildLandingPageRoutePure = ({ parameters, path, mappingId }: OpRoute, override?: Partial<OpRoute>): Route => {
  const apiParams = mapParameters(parameters || []);
  return {
    component: LandingPage,
    path,
    name: `LandingPage_${mappingId}`,
    exact: true,
    mappingId,
    parameters,
    fetchInitialData: fetchLandingPageInitialData({
      categoryId: apiParams.get('categoryId'),
      categoryName: apiParams.get('categoryName'),
    }),
    ...override,
  };
};

const buildLandingPageRoute = (route: OpRoute): Route[] => {
  switch (route.mappingId) {
    case 'products.ShowLandingPage(category="virtual_category/home_b")':
      return [buildLandingPageRoutePure(route), buildLandingPageRoutePure(route, { path: '/' })];
    default:
      return [buildLandingPageRoutePure(route)];
  }
};

const buildProductRoute = ({ path }: OpRoute) => ({
  path: `${path.replace(/{([a-z_-]+)}/g, ':$1')}`,
  name: 'ProductPage',
  exact: true,
  component: ProductPage,
  fetchInitialData: fetchProductPageInitialData,
});

const buildStudioProductRoute = ({ path }: OpRoute) => ({
  path: `${path.replace(/{([a-z_A-Z-]+)}/g, ':$1')}`,
  name: 'StudioProductPage',
  exact: true,
  component: StudioSearchProductPage,
});

const buildCartRoute = (route: OpRoute) =>
  ({
    categoryId: undefined,
    path: route.path,
    name: 'Basket',
    exact: true,
    component: BasketPage,
    fetchInitialData: undefined,
  } as Route);

const buildStudioSearchPage = (route: OpRoute): Route => {
  return {
    ...route,
    component: StudioSearchPageRoute,
    name: `StudioSearchPage_${route.mappingId}`,
    exact: true,
    fetchInitialData: fetchStudioSearchPageInitialData,
  };
};

const buildOpRoutes = (opRoutes: OpRoute[]): Route[] => {
  return opRoutes
    .flatMap((route: OpRoute) => {
      switch (route.type as RouteType) {
        case RouteType.BASKET:
          return buildCartRoute(route);
        case RouteType.PRODUCT_PAGE:
          return buildProductRoute(route);
        case RouteType.LANDING_PAGE:
          return buildLandingPageRoute(route);
        case RouteType.PERSONALIZED_PRODUCT_PAGE:
          return buildPersonalisedProductsRoute(route);
        case RouteType.REACT_PAGE:
          return buildStudioProductRoute(route);
        case RouteType.STUDIO_SEARCH_PAGE:
          return buildLocalizedRoute({
            ...route,
            component: SearchPage,
            name: 'SearchPage',
            exact: true,
            fetchInitialData: fetchSearchPageInitialData,
          });
        case RouteType.ROUTE: {
          const initialRoute = initialRoutes.find((i) => i.mappingId === route.mappingId);
          return initialRoute
            ? buildLocalizedRoute({
                ...initialRoute,
                ...route,
              })
            : buildPageNotFoundRoute();
        }
        default: {
          return buildPageNotFoundRoute();
        }
      }
    })
    .filter((route: Route) => route);
};

const composeRoutes = (categories: Category[], opRoutes: OpRoute[]) => [
  ...initialRoutes,
  ...buildOpRoutes(opRoutes),
  ...buildCategoryRoutes(categories),
];

export default composeRoutes;
